import { Attribute, AttributesResource, AttributeTypeEnumType } from '@mark43/rms-api';
import { useMemo } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { toAttributeOption } from './to-attribute-option';
import { useAttributesByType } from './use-attributes-by-type';
import { AttributeOption } from '@mark43/cad-api';

export interface AttributeOptionWithType extends AttributeOption {
    attributeType: AttributeTypeEnumType;
}

interface UseAttributeOptionsByTypeReturnType extends Omit<UseQueryResult<Attribute[]>, 'data'> {
    attributeOptions: AttributeOptionWithType[];
}

export function useAttributeOptionsByType({
    attributeType,
    parentAttributeId,
    includeDisplayAbbreviation = false,
    params,
}: {
    attributeType: AttributeTypeEnumType;
    parentAttributeId?: number;
    includeDisplayAbbreviation?: boolean;
    params?: AttributesResource.GetAttributesForTypes['params'];
}): UseAttributeOptionsByTypeReturnType {
    const { attributes, ...rest } = useAttributesByType({
        attributeType,
        parentAttributeId,
        params,
    });

    const attributeOptions = useMemo(() => {
        return attributes.map((attr) => {
            const option = toAttributeOption(attr, { includeDisplayAbbreviation });
            return {
                ...option,
                attributeType,
            } as AttributeOptionWithType;
        });
    }, [attributes, includeDisplayAbbreviation, attributeType]);

    return { attributeOptions, ...rest };
}
